import React, { FC, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../atoms/Spinner";
import { service } from "../../../services/AxiosServices";
import SectionHeader from "./SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import MainLayout from "../../Layouts/MainLayout";
import Label from "../atoms/Label";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-country-state-city/dist/react-country-state-city.css";
import { CountrySelect } from "react-country-state-city";
import ShoweyeIcon from "../../static/svg/ShoweyeIcon";
import HideeyeIcon from "../../static/svg/HideeyeIcon";
import { FormState, AdminFormState} from '../types/User';
const OrganizationForm: FC = () => {
  const [searchParams] = useSearchParams();
  const companyId = Number(searchParams.get("id"));
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadSave, setLoadSave] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [countryId, setCountryid] = useState<number | undefined>(undefined);
  const [country, setCountry] = useState<string>("Belgium");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [adminPhoneNumber, setAdminPhoneNumber] = useState("");
  const [errors, setErrors] = useState<{}>({});
  const [adminErrors, setAdminErrors] = useState<{}>({});
  const [companies, setCompanies] = useState<{ id: number; company_name: string }[]>([]);
  const [phoneCountry, setPhoneCountry] = useState("be");
  const [serverError, setServerError] = useState<string>("");
  const [isDisabled, setisDisabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()[\]{}\-_=+|;:',.<>/?]).{12,}$/;;
  const textWithSpaceDotRegex = /^[A-Za-z0-9\s./À-ÿ'’&-]*$/;
  const [adminFormState, setAdminFormState] = useState<AdminFormState>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  })
  const [formState, setFormState] = useState<FormState>({
    companyName: "",
    companyLegalName: "",
    email: "",
    phoneNumber: "",
    companyWebsite: "",
    street: "",
    houseNumber: "",
    boxNumber: "",
    city: "",
    country: "",
    postal: "",
  });

  useEffect(() => {
    if (companyId) {
      const fetchCompany = async () => {
        setLoading(true);
        try {
          const response = await service<{ response: any }>(
            "/api/get-company",
            "post",
            { company_id: companyId }
          );
          const company = response.data?.response;
          if (company) {
            setFormState({
              companyName: company.name || "",
              companyLegalName: company.legal_name || "",
              email: company.email?.replace("mailto:", "") || "",
              phoneNumber: company.phone || "",
              companyWebsite: company.website || "",
              street: company.address?.street_name || "",
              houseNumber: company.address?.house_number || "",
              boxNumber: company.address?.box_number || "",
              city: company.address?.city || "",
              country: company.address?.country || "",
              postal: company.address?.postal_code || "",
            });
            setAdminFormState({
              firstName: company.company_admin?.first_name || "",
              lastName: company.company_admin?.last_name || "",
              email: company.company_admin?.email?.replace("mailto:", "") || "",
              password: company.company_admin?.password || "",
            });
            setCountry(company.address?.country || "");
          }
        } catch (err) {
          console.error("Error fetching data:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchCompany();
    }
  }, [companyId]);
  
  useEffect(() => {
    const fetchPartnerCompanies = async () => {
      try {
        const response = await service<{ response: { id: number; company_name: string }[] }>(
          "/get-partner-companies",
          "get"
        );
  
        const partnerCompanies = (response.data?.response || []).sort((a, b) =>
          a.company_name.localeCompare(b.company_name)
        );
  
        setCompanies(partnerCompanies);
      } catch (err) {
        console.error("Error fetching partner companies:", err);
      }
    };
  
    fetchPartnerCompanies();
  }, []);

  const handleGenericInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    setState: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const { name, value } = e.target;
    setState((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenericPhoneChange = (
    value: string,
    data: any,
    setPhone: React.Dispatch<React.SetStateAction<string>>,
    setForm: React.Dispatch<React.SetStateAction<any>>
  ) => {
    setPhone(value);

    if (data?.countryCode) {
      setCountry(data.name);
      setForm((prev: any) => ({
        ...prev,
        country: data.name,
      }));
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePhoneNumber = (phone: string): string | null => {
    if (!phone) return "Phone number is required.";

    const formattedPhoneNumber = phone.startsWith("+") ? phone : `+${phone}`;
    const phoneRegex = /^\+?[1-9]\d{0,3}[-.\s]?\d{4,}$/;

    if (!phoneRegex.test(formattedPhoneNumber)) return "Please enter a valid phone number.";

    const validCountryCodes = new Set([
      "1", "93", "355", "213", "32", "44", "91", "61", "81", "966", "27", "82", "211", "34", "94", "46",
      "92", "1268", "376", "244", "54", "374", "297", "43", "994", "1242", "1246", "880", "973", "375",
      "501", "229", "975", "591", "387", "267", "55", "264", "673", "359", "226", "257", "855", "237", "86", "57",
      "45", "20", "358", "33", "49", "30", "354", "62", "98", "964", "353", "39", "1876", "962", "254", "965", "218",
      "60", "52", "95", "64"
    ]);

    let enteredCountryCode = "";
    for (let code of Array.from(validCountryCodes)) {
      if (formattedPhoneNumber.startsWith("+" + code)) {
        enteredCountryCode = code;
        break;
      }
    }
    if (!enteredCountryCode) return "Please enter a valid country code or select the correct country.";
    return null;
  };

  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};
  
    if (!formState.companyName) newErrors.companyName = "Company name is required.";

  
    if (!formState.companyLegalName) newErrors.companyLegalName = "Company legal name is required.";
    if (!formState.email) newErrors.email = "This email field is required.";
    else if (!isValidEmail(formState.email)) newErrors.email = "Incorrect email format.";
    
  
    if (phoneNumber && phoneNumber.trim() !== "") {
      const phoneError = validatePhoneNumber(phoneNumber);
      if (phoneError)  newErrors.phoneNumber = phoneError;
    }
  
    if (!formState.houseNumber)newErrors.houseNumber = "House number is required.";
    else if (!textWithSpaceDotRegex.test(formState.houseNumber)) newErrors.houseNumber = "Invalid house number format.";
    else if (formState.houseNumber.length > 50) newErrors.houseNumber = "House number must be 50 characters or fewer.";
    
  
    if (!formState.street) {
      newErrors.street = "Street name is required.";
    } else if (!textWithSpaceDotRegex.test(formState.street)) {
      newErrors.street = "Invalid street name format.";
    } else if (formState.street.length > 255) {
      newErrors.street = "Street name must be 255 characters or fewer.";
    }
  
    if (formState.boxNumber && !textWithSpaceDotRegex.test(formState.boxNumber)) {
      newErrors.boxNumber = "Invalid box number format.";
    } else if (formState.boxNumber && formState.boxNumber.length > 50) {
      newErrors.boxNumber = "Box number must be 50 characters or fewer.";
    }
  
    if (!formState.postal) {
      newErrors.postal = "Postal code is required.";
    } else if (!textWithSpaceDotRegex.test(formState.postal)) {
      newErrors.postal = "Invalid postal code format.";
    } else if (formState.postal.length > 50) {
      newErrors.postal = "Postal code must be 50 characters or fewer.";
    }
  
    if (!formState.city) {
      newErrors.city = "City is required.";
    } else if (!textWithSpaceDotRegex.test(formState.city)) {
      newErrors.city = "Invalid city name format.";
    } else if (formState.city.length > 255) {
      newErrors.city = "City name must be 255 characters or fewer.";
    }
  
    if (!formState.country) {
      newErrors.country = "Country is required.";
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAdminForm = () => {
    const newErrors: Record<string, string> = {};

    if (!adminFormState.firstName.trim()) newErrors.firstName = "First name is required.";
    if (!adminFormState.lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!adminFormState.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!isValidEmail(adminFormState.email)) {
      newErrors.email = "Invalid email format.";
    }
    if (!adminFormState.password.trim()) {
      newErrors.password = "Password is required.";
    } else if (!passwordRegex.test(adminFormState.password)) {
      newErrors.password = `Password must be at least 12 characters and include one uppercase letter, one number, and one special character.`;
    }

    if (adminPhoneNumber && adminPhoneNumber.trim() !== "") {
      const phoneError = validatePhoneNumber(adminPhoneNumber);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }
    setAdminErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const companyTab = document.getElementById("company-details-tab") as HTMLElement;
      if (companyTab) companyTab.click();
      return;
    }
    if (!validateAdminForm()) return;
    try {
      setLoadSave(true);
      const finalPayload = {
        ...formData,
        company_admin: {
          first_name: adminFormState.firstName,
          last_name: adminFormState.lastName,
          email: adminFormState.email,
          phone: adminPhoneNumber,
          password: adminFormState.password,
        },
      };
  
      console.log("Final Payload:", finalPayload);
  
      const response = await service<any>("/api/add-tenants/", "post", finalPayload);
      console.log("Response:", response);
  
      toast.success("Company created successfully!", { autoClose: 2000 });
      setServerError("");
      navigate("/");
    } catch (err: any) {
      console.error("Error:", err);
      const res = err?.response?.data;
      const msg = res
        ? Object.entries(res)
            .map(([k, v]) => {
              const str = Array.isArray(v) ? v.join(" ") : v;
              if (typeof str === "string") {
                return str.charAt(0).toUpperCase() + str.slice(1);
              }
              return str;
            })
            .join(" | ")
        : "Something went wrong. Please try again.";
        setServerError(msg);
    } finally{
      setLoadSave(false);
    }
  };
  
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const formattedData = {
        name: formState.companyName,
        legal_name: formState.companyLegalName,
        email: formState.email,
        phone: phoneNumber,
        website: formState.companyWebsite,
        partner_company: selectedCompany,
        address: {
          street_name: formState.street,
          house_number: formState.houseNumber,
          box_number: formState.boxNumber,
          postal_code: formState.postal,
          city: formState.city,
          country: country,
        },
      };

      setFormData(formattedData);
      setisDisabled(false);
      setTimeout(() => {
        const adminTab = document.getElementById("admin-tab") as HTMLElement;
        if (adminTab) adminTab.click();
      }, 0); 
    } catch (err: any) {
      console.error("Error:", err);
    }
  };
  
  const handleCancel = () => {
    navigate(-1);
  };
  const clickPrevious = () => {
    setisDisabled(true);
    setTimeout(() => {
      const adminTab = document.getElementById("company-details-tab") as HTMLElement;
      if (adminTab) {
        adminTab.click();
      }
    }, 0);
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  


  return (
    <MainLayout excludeSidebar={true}>
      <div className="row ms-1">
        <SectionHeader
          back={true}
          title={companyId ? "Edit company" : "Create company"}
        />
      </div>
      <WindowHeightComponent className="ms-3">
        <div className="p-2">
          <ul className="nav nav-tabs border-0 mb-4 p-2" id="myTab" role="tablist">
            <li className="nav-item me-3" role="presentation">
              <button className="nav-link Tab-btn active" id="company-details-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true" disabled={!isDisabled}>Company details</button>
            </li>
            <li className="nav-item" role="presentation">
              <button className="nav-link Tab-btn" id="admin-tab" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile" aria-selected="false" disabled={isDisabled}>Company admin</button>
            </li>
          </ul>

          <div className="tab-content p-2" id="myTabContent">
            <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="company-details-tab">
              <form onSubmit={handleSubmit} className="space-y-4">
                {Object.keys(errors).length > 0 && (
                  <div className="alert alert-danger">
                    <ul>
                      {Object.values(errors).map((err, index) => (
                        <li key={index}>{typeof err === "string" ? err : JSON.stringify(err)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mb-3">
                  <p className="company-title">Company details</p>
                </div>
                <div className="row">
                  <div className="col-6 mb-3">
                    <Label htmlFor="organizationName">
                      Company name:<span style={{ color: "red" }}>*</span>
                    </Label>
                    <input
                      type="text"
                      name="companyName"
                      value={formState.companyName}
                      onChange={(e) => handleGenericInputChange(e, setFormState)}
                      className="form-control"
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <Label htmlFor="organizationName">
                      Company legal name:<span style={{ color: "red" }}>*</span>
                    </Label>
                    <input
                      type="text"
                      name="companyLegalName"
                      value={formState.companyLegalName}
                      onChange={(e) => handleGenericInputChange(e, setFormState)}
                      className="form-control"
                    />
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="email">
                        Email:<span style={{ color: "red" }}>*</span>
                      </Label>
                      <input
                        type="text"
                        name="email"
                        value={formState.email}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="phoneNumber">Phone number:</Label>
                      <PhoneInput
                        country={phoneCountry}
                        onChange={(value: string, data: any, event: any, formattedValue: string) => {
                          setPhoneNumber(value);
                          setPhoneCountry(data.countryCode);
                          setFormState((prev: any) => ({
                            ...prev,
                            phoneNumber: value,
                          }));
                        }}
                        inputStyle={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="email">
                        Company website:
                      </Label>
                      <input
                        type="text"
                        name="companyWebsite"
                        value={formState.companyWebsite}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
                <br></br>
                <div className="mb-3">
                  <p className="company-title">Company address</p>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="houseNumber">House number:<span style={{ color: "red" }}>*</span></Label>
                      <input
                        type="text"
                        name="houseNumber"
                        value={formState.houseNumber}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="streetAddress">Street name:<span style={{ color: "red" }}>*</span></Label>
                      <input
                        type="text"
                        name="street"
                        value={formState.street}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="boxNumber">Box number</Label>
                      <input
                        type="text"
                        name="boxNumber"
                        value={formState.boxNumber}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="postal">Postal code:<span style={{ color: "red" }}>*</span></Label>
                      <input
                        type="text"
                        name="postal"
                        value={formState.postal}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="city">City:<span style={{ color: "red" }}>*</span></Label>
                      <input
                        type="text"
                        name="city"
                        value={formState.city}
                        onChange={(e) => handleGenericInputChange(e, setFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="col-6">

                    <div className="mb-3">
                      <Label htmlFor="country">Country:<span style={{ color: "red" }}>*</span></Label>
                      <div className="border-none">
                        <CountrySelect
                          onChange={(e: any) => {
                            if (e && typeof e === "object" && "id" in e && "name" in e) {
                              setCountry(e.name);
                              setCountryid(e.id);
                              setFormState((prev: any) => ({
                                ...prev,
                                country: e.name,
                              }));
                            }
                          }}
                          containerClassName="w-full"
                          inputClassName="w-full border-none rounded-md"
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "none",
                            padding: 0,
                            borderRadius: "5px",
                          }}
                          placeHolder="Select Country"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <br></br>
                <div className="mb-3">
                  <p className="company-title">Partner</p>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="mb-3 w-100">
                      <Label htmlFor="Partner company">Partner company</Label>
                      <select
                        className="form-select"
                        value={selectedCompany}
                        onChange={(e) => {
                          setSelectedCompany(e.target.value);
                        }}>
                        <option value="">Select a company</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.company_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="submit" className="btn SaveBtn">
                    Next
                  </button>
                </div>
              </form>
            </div>
            <div className="tab-pane  fade" id="profile" role="tabpanel" aria-labelledby="admin-tab">
              <form onSubmit={handleAdminSubmit} className="space-y-4">
                <div className="p-3">
                {serverError && (
                  <div className="alert alert-danger">
                    <ul>
                      {serverError.split(" | ").map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  </div>
                )}
                {Object.keys(adminErrors).length > 0 && (
                  <div className="alert alert-danger">
                    <ul>
                      {Object.values(adminErrors).map((err, index) => (
                        <li key={index}>{typeof err === "string" ? err : JSON.stringify(err)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                </div>
                <p className="company-title">Company admin</p>
                <div className="row">
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="organizationName">
                        First name:<span style={{ color: "red" }}>*</span>
                      </Label>
                      <input
                        type="text"
                        name="firstName"
                        value={adminFormState.firstName}
                        onChange={(e) => handleGenericInputChange(e, setAdminFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="organizationName">Last name:<span style={{ color: "red" }}>*</span></Label>
                      <input
                        type="text"
                        name="lastName"
                        value={adminFormState.lastName}
                        onChange={(e) => handleGenericInputChange(e, setAdminFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="email">
                        Email:<span style={{ color: "red" }}>*</span>
                      </Label>
                      <input
                        type="text"
                        name="email"
                        value={adminFormState.email}
                        onChange={(e) => handleGenericInputChange(e, setAdminFormState)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="email">
                        Password:<span style={{ color: "red" }}>*</span>
                      </Label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={adminFormState.password}
                          onChange={(e) => handleGenericInputChange(e, setAdminFormState)}
                          className="form-control"
                        />
                        <span
                          className="Lgin-eyeicon mt-2 position-absolute"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? <ShoweyeIcon /> : <HideeyeIcon />}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <Label htmlFor="phoneNumber">Phone number:</Label>
                      <PhoneInput
                        country={"be"}
                        value={adminPhoneNumber}
                        onChange={(value: string, data: any) =>
                          handleGenericPhoneChange(value, data, setAdminPhoneNumber, setAdminFormState)
                        }
                        inputStyle={{ width: "100%" }}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-3">
                    <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-secondary me-2" onClick={clickPrevious}>
                      Previous
                    </button>
                    <button type="submit" className="btn SaveBtn" disabled={loadSave}>
                    {loadSave ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm me-2 d-inline-block"
                          role="status"
                        >
                        </div>
                        <span className="d-inline-block">Saving...</span>
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </WindowHeightComponent>
    </MainLayout>
  );
};

export default OrganizationForm;